/* ============================================================================
   assets/aoora-scroll.js
   ============================================================================
   Shared scroll-animation engine for all aoora sections. Mirrors the
   artifact's scroll math with three primitives (CSS states live in
   aoora-base.css, so content is always visible even if this file fails):

   1. WORD FILL — progressive word-by-word color sweep.
        <p class="aoora-fill" data-aoora-fill data-start=".25" data-len=".55">
          <span>Word </span><span>word </span>…
        </p>
      start/len are fractions of the parent section's scroll progress.

   2. FOCUS — blur-to-sharp toggle at a progress threshold.
        <div class="aoora-focus" data-aoora-focus data-at=".3">…</div>

   3. REVEAL — rise-in once when entering the viewport (IntersectionObserver).
        <div class="aoora-reveal" data-aoora-reveal data-delay="120">…</div>

   Sections opt in with data-aoora-scroll on their root element; fill/focus
   progress is computed per-section: p = (vh - rect.top) / (vh + rect.height).

   Idempotent + Theme Editor aware (shopify:section:load re-scans). Exits
   entirely under prefers-reduced-motion or body.aoora-no-anim — the CSS
   kill-switches already resolve every element to its final visible state.
   ============================================================================ */

(() => {
  if (window.AooraScroll) return;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const killed = document.body.classList.contains('aoora-no-anim');
  if (reduced || killed) {
    window.AooraScroll = { disabled: true, refresh() {} };
    return;
  }

  const sections = new Set();
  let ticking = false;

  /* ── Reveal primitive (viewport-entry, latches once) ────────────────── */
  const revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        const delay = parseInt(el.dataset.delay || '0', 10);
        if (delay) el.style.transitionDelay = `${delay}ms`;
        el.classList.add('on');
        revealObserver.unobserve(el);
      });
    },
    { threshold: 0.18, rootMargin: '0px 0px -4% 0px' }
  );

  /* ── Progress-driven primitives (fill + focus) ──────────────────────── */
  const clamp01 = (n) => Math.min(1, Math.max(0, n));

  function progressOf(el) {
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight;
    return clamp01((vh - rect.top) / (vh + rect.height));
  }

  function update() {
    ticking = false;
    sections.forEach((section) => {
      if (!section.isConnected) {
        sections.delete(section);
        return;
      }
      const p = progressOf(section);

      section.querySelectorAll('[data-aoora-fill]').forEach((fill) => {
        const start = parseFloat(fill.dataset.start || '.15');
        const len = parseFloat(fill.dataset.len || '.6');
        const local = clamp01((p - start) / len);
        const spans = fill.children;
        const n = Math.round(local * spans.length);
        for (let i = 0; i < spans.length; i++) {
          spans[i].classList.toggle('on', i < n);
        }
      });

      section.querySelectorAll('[data-aoora-focus]').forEach((focus) => {
        const at = parseFloat(focus.dataset.at || '.25');
        focus.classList.toggle('on', p >= at);
      });
    });
  }

  function requestUpdate() {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(update);
    }
  }

  /* ── Scan / rescan ───────────────────────────────────────────────────── */
  function collect(root) {
    const scope = root || document;
    scope.querySelectorAll('[data-aoora-scroll]').forEach((s) => sections.add(s));
    if (scope !== document && scope.matches && scope.matches('[data-aoora-scroll]')) {
      sections.add(scope);
    }
    scope.querySelectorAll('[data-aoora-reveal]').forEach((el) => {
      if (!el.classList.contains('on')) revealObserver.observe(el);
    });
    requestUpdate();
  }

  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', requestUpdate, { passive: true });

  /* Theme Editor: re-scan reloaded sections */
  document.addEventListener('shopify:section:load', (e) => collect(e.target));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => collect());
  } else {
    collect();
  }

  window.AooraScroll = { refresh: collect, disabled: false };
})();